import { GetServerSideProps } from 'next';
import { useEffect, useState } from 'react';
import Head from 'next/head';
import {
  DevicePageT,
  DeviceT,
  DeviceVariantT,
  PageType,
} from '@/lib/buyback/types';
import { generateSteps, generateVariants } from '@/lib/buyback/helpers';
import Layout from '@/components/buyback/v2/Layout';
import settings from '@/settings';
import { getOffersBySlug } from '@/lib/backend/buybackListings';
import SelectVariant from '@/components/buyback/v2/sell/SelectVariant';
import { useBuybackContext } from '@/lib/buyback/BuybackContext';
import Schema from '@/lib/buyback/Seo';
import { REDIRECT_HOME } from '@/lib/common';

export const getServerSideProps: GetServerSideProps<DevicePageT> = async (
  event,
) => {
  if (!['reebelo-us', 'reebelo-dev'].includes(settings.store))
    return REDIRECT_HOME;

  const handle = event.query.handle as string;

  // Retrieve a Device
  const data: { devices: DeviceT[]; variants: DeviceVariantT[] } = {
    devices: [],
    variants: [],
  };

  const offers: DeviceT[] = await getOffersBySlug(handle);

  data.devices = offers;
  data.variants = generateVariants(offers);

  if (data.devices?.length) {
    const device = {
      title: data.devices[0].model.split(' - ')[0],
      handle: data.devices[0].slug,
      brand: data.devices[0].brand,
      category: data.devices[0].category,
    };

    return {
      props: {
        device,
        variants: data.variants,
        devices: data.devices,
      },
    };
  }

  return REDIRECT_HOME;
};

const DevicePage = (props: DevicePageT) => {
  const { resetQuote, isLoading } = useBuybackContext();
  const [subStep, setSubStep] = useState(0);
  const steps = generateSteps(props.variants);

  // Reset Context
  useEffect(() => {
    if (!isLoading) resetQuote();
  }, [isLoading]);

  return (
    <>
      <Head>
        <title>{`Sell Your ${props.device.title} on Reebelo - Quick & Easy BuyBack Process`}</title>
        {/* Disable auto zoom in on input focus  */}
        <meta
          name="viewport"
          content="width=device-width, initial-scale=1.0, maximum-scale=1.0, user-scalable=no"
        />
        <Schema />
      </Head>
      <div className="text-sm text-gray-700">
        <Layout
          device={props.device}
          step={3}
          pageType={PageType.VARIANT}
          steps={steps}
          subStep={subStep}
        >
          <SelectVariant
            progress={{
              steps,
              step: 3,
              subStep,
              setSubStep,
            }}
            device={props.device}
            variants={props.variants as DeviceVariantT[]}
            pageType={PageType.VARIANT}
            devices={props.devices}
          />
        </Layout>
      </div>
    </>
  );
};

export default DevicePage;
